import get from 'lodash/get';
import isArray from 'lodash/isArray';
import alertActions from 'optinmonster-js-library/src/vue/campaigns/store/alerts/actions';
import { getAppData, siteId } from '@/utils';

export default {
	...alertActions,
	fetchMe({ state, commit, dispatch, getters }, { loading = null } = {}) {
		if (null === loading) {
			loading = !getters.connected;
		}

		if (loading) {
			commit('loading', 'me');
		}

		return new Promise((resolve, reject) => {
			if (!state.apiKey) {
				commit('doneLoading', 'me');
				return resolve();
			}
			return this._vm.$api
				.get({ wpEndpoint: 'omapp/v1/me' })
				.then(({ body }) => {
					commit('setUser', body);
					commit('doneLoading', 'me');

					const types = get(body, 'campaignTypes');
					if (types && isArray(types)) {
						commit('templates/permittedTypes', types);
					}

					dispatch('notifications/fetch');

					this._vm.$bus.$emit('fetchedMe');
					resolve();
				})
				.catch((error) => {
					commit('maybeAddError', error);
					commit('doneLoading', 'me');
					reject(error);
					// eslint-disable-next-line no-console
					console.error({ error });
				});
		});
	},
	updateMe({ state, getters, commit }) {
		return new Promise((resolve) => {
			if (getters.isFetched('updateMe') || getters.isFetching('updateMe')) {
				return resolve();
			}
			commit('fetching', 'updateMe', { root: true });

			return this._vm.$api
				.post({ wpEndpoint: 'omapp/v1/settings' })
				.send({
					settings: {
						accountId: get(state, 'user.accountId'),
						currentLevel: get(state, 'user.currentLevel'),
						plan: get(state, 'user.plan'),
					},
				})
				.then(() => {
					commit('fetched', 'updateMe', { root: true });
					resolve();
				})
				.catch((error) => {
					commit('doneFetching', 'updateMe', { root: true });
					// eslint-disable-next-line no-console
					console.error('Failed updating user:', error);
				});
		});
	},
	saveApiKey({ commit }, key) {
		commit('loading', 'connect');
		return new Promise((resolve, reject) => {
			const handleError = (error) => {
				this._vm.$bus.$emit('saveApiKeyFailed', key);
				commit('closeAlerts', null);

				error.explanation = '<strong>Something went wrong while attempting to connect</strong>';
				commit('setError', error);
				commit('doneLoading', 'connect');
				reject(error);
				// eslint-disable-next-line no-console
				console.error({ error });
			};

			const success = ({ body }) => {
				this._vm.$bus.$emit('saveApiKeySuccess', key, body);
				commit('doneLoading', 'connect');

				resolve();
			};

			commit('setApiKey', key);

			return this._vm.$api
				.post({ wpEndpoint: 'omapp/v1/api/' })
				.unset('X-OptinMonster-ApiKey')
				.send({ key })
				.then(success)
				.catch(handleError);
		});
	},
	disconnect({ commit }) {
		commit('loading', 'disconnect');
		return new Promise((resolve, reject) => {
			const handleError = (error) => {
				commit('closeAlerts', null);

				error.explanation = '<strong>Something went wrong while attempting to disconnect</strong>';
				commit('setError', error);
				commit('doneLoading', 'disconnect');
				reject(error);
				// eslint-disable-next-line no-console
				console.error({ error });
			};

			const success = () => {
				this._vm.$bus.$emit('disconnectSuccess');
				commit('setApiKey', '');
				commit('resetUser');
				commit('doneLoading', 'disconnect');
				resolve();
			};

			return this._vm.$api
				.delete({ wpEndpoint: 'omapp/v1/api/' })
				.unset('X-OptinMonster-ApiKey')
				.then(success)
				.catch(handleError);
		});
	},
	triggerSiteUpdateVerification({ commit, getters }) {
		return new Promise((resolve, reject) => {
			if (!getters.connected) {
				return resolve();
			}

			let endpoint = `sites/${siteId}/update-verification`;
			endpoint += `?site_url=${encodeURIComponent(this._vm.$urls.wp())}`;
			endpoint += `&rest_url=${encodeURIComponent(this._vm.$urls.wpRest())}`;

			return this._vm.$api
				.get({ endpoint, cached: false })
				.then(({ body }) => {
					resolve(body);
				})
				.catch((error) => {
					error.explanation = 'Site update verification failed.';
					error.closes = true;
					commit('setError', error);
					reject(error);
					// eslint-disable-next-line no-console
					console.error({ error });
				});
		});
	},
	setTitle({ commit }, { title, setBannerTitle = true }) {
		document.title = getAppData('titleTag', title).replace('{replaceme}', title);

		if (setBannerTitle) {
			commit('setBannerTitle', title);
		}
	},
	dismissReview({ commit }, later = false) {
		commit('loading', 'dismiss-review');

		return new Promise((resolve) => {
			return this._vm.$api
				.post({ wpEndpoint: 'omapp/v1/review/dismiss' })
				.send({ later })
				.then(() => {
					commit('doneLoading', 'dismiss-review');
					commit('reviewDismissed');
					resolve();
				})
				.catch((error) => {
					commit('doneLoading', 'dismiss-review');
					// eslint-disable-next-line no-console
					console.error('Failed dismissing review notice:', error);
				});
		});
	},
};
